/**
 * PDF 解析（浏览器端，使用 pdfjs-dist）
 * ------------------------------------------------------------
 * 只负责把 PDF 变成「带结构的文本行」，不做内容理解：
 *   parsePDF(file) -> { pages: [{ page, lines: [{ text, x, y, size, bold }], hasText }] }
 *
 * 能力边界（务必清楚）：
 *   - 有文字层的 PDF（出版社电子版、Word 导出）：识别效果好
 *   - 扫描版 / 图片版 PDF：没有文字层，这里会明确标记 hasText=false，
 *     由 UI 提示「此文件需要 OCR 能力，请改用文字版或手工录入」
 *   - 复杂双栏/表格版面：按坐标聚行，可能猜错顺序，需人工在预览页修正
 *
 * 换 Worker：不再依赖 vendor/ 目录，由 Vite 处理静态资源。
 * 单文件离线版（双击打开）无法加载外部 worker，pdf.js 会自动回退到主线程解析。
 */
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

/** 读取文件为 ArrayBuffer */
function readFile(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsArrayBuffer(file);
  });
}

/**
 * 把 pdfjs 的 textItem 聚合成行
 * 思路：按 y 坐标分组（容差 = 字号的一半），组内按 x 排序，间距大则补空格
 */
function itemsToLines(items) {
  const rows = [];
  for (const it of items) {
    if (!it.str || !it.str.trim()) continue;
    const [, , , , x, y] = it.transform;
    const size = Math.abs(it.transform[3]) || it.height || 10;
    const bold = /bold|black|heavy/i.test(it.fontName || '');
    const tol = Math.max(2, size * 0.5);
    let row = rows.find(r => Math.abs(r.y - y) <= tol);
    if (!row) { row = { y, size, items: [] }; rows.push(row); }
    row.items.push({ x, w: it.width || 0, text: it.str, size, bold });
  }

  rows.sort((a, b) => b.y - a.y);   // PDF 坐标 y 向上，倒序即从上到下

  return rows.map(r => {
    r.items.sort((a, b) => a.x - b.x);
    let text = '';
    let lastEnd = null;
    for (const it of r.items) {
      if (lastEnd !== null) {
        const gap = it.x - lastEnd;
        if (gap > it.size * 0.35) text += ' ';
      }
      text += it.text;
      lastEnd = it.x + it.w;
    }
    return {
      text: text.replace(/\s+/g, ' ').trim(),
      x: r.items[0]?.x ?? 0,
      y: r.y,
      size: r.size,
      bold: r.items.some(i => i.bold),
    };
  }).filter(l => l.text);
}

/**
 * 解析 PDF
 * @param {File} file
 * @param {(p:{page:number,total:number})=>void} [onProgress]
 * @returns {Promise<{name:string, pageCount:number, pages:Array, textLength:number, hasText:boolean}>}
 */
export async function parsePDF(file, onProgress) {
  const buf = await readFile(file);
  const doc = await pdfjsLib.getDocument({ data: buf }).promise;
  const pages = [];
  let textLength = 0;

  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    const lines = itemsToLines(content.items);
    textLength += lines.reduce((n, l) => n + l.text.length, 0);
    pages.push({ page: p, lines, hasText: lines.length > 0 });
    onProgress?.({ page: p, total: doc.numPages });
    page.cleanup();
  }

  return {
    name: file.name || 'unnamed.pdf',
    pageCount: doc.numPages,
    pages,
    textLength,
    // 平均每页少于 12 个字符 → 判定为疑似扫描件
    hasText: textLength > doc.numPages * 12,
  };
}

/** 把解析结果转成纯文本（便于预览与粘贴） */
export function pdfToText(parsed) {
  return parsed.pages
    .map(p => `【第 ${p.page} 页】\n` + p.lines.map(l => l.text).join('\n'))
    .join('\n\n');
}

/** 纯文本文件（TXT / MD）也走同一套流程，方便测试与手工导入 */
export async function parseTextFile(file) {
  const text = await file.text();
  const lines = text.split(/\r?\n/).map((t, i) => ({ text: t.trim(), x: 0, y: -i, size: 12, bold: false }))
    .filter(l => l.text);
  return {
    name: file.name || 'unnamed.txt',
    pageCount: 1,
    pages: [{ page: 1, lines, hasText: lines.length > 0 }],
    textLength: text.length,
    hasText: lines.length > 0,
  };
}

/**
 * 统一入口：按扩展名/类型选择解析器
 */
export async function parseDocument(file, onProgress) {
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.pdf') || file.type === 'application/pdf') {
    return parsePDF(file, onProgress);
  }
  if (name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.csv') || file.type.startsWith('text/')) {
    return parseTextFile(file);
  }
  throw new Error(`不支持的文件类型：${file.name || file.type}。目前支持 PDF / TXT / MD。`);
}
